/**
 * 欢迎页两阶段加载编排：
 * - critical：首屏可见关键（并行高并发）
 * - silent：次要数据延后/空闲时静默填充，不抢首屏带宽
 */
import {
  loadWelcomeOperationSlots,
  loadWelcomeDevices,
} from './welcomeShellData';
import {
  loadHomeFarmPreviewShell,
  enrichHomeFarmPreview,
} from './homeFarmPreview';

/** 静默阶段：等首屏请求先占住连接；timeout 兜底避免 idle 过晚 */
export function scheduleWelcomeSilent(fn, { delayMs = 180, idleTimeoutMs = 1200 } = {}) {
  let idleId = 0;
  let timerId = 0;
  let cancelled = false;

  const run = () => {
    if (cancelled) return;
    try {
      fn();
    } catch {
      /* ignore */
    }
  };

  timerId = window.setTimeout(() => {
    if (cancelled) return;
    if (typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(run, { timeout: idleTimeoutMs });
    } else {
      run();
    }
  }, delayMs);

  return () => {
    cancelled = true;
    if (timerId) window.clearTimeout(timerId);
    if (idleId && typeof window.cancelIdleCallback === 'function') {
      window.cancelIdleCallback(idleId);
    }
  };
}

/**
 * 首屏关键并行：运营位 + 农场壳层（各 1～2 请求，Promise.all）。
 * @returns {Promise<{ slots: object, shellCtx: object|null }>}
 */
export async function loadWelcomeCriticalParallel({
  loggedIn,
  useJwtTenant = false,
  devicesOnline = 0,
  signal,
} = {}) {
  const [slots, shellCtx] = await Promise.all([
    loadWelcomeOperationSlots().catch(() => ({ carousel: [], topic: [] })),
    loadHomeFarmPreviewShell(loggedIn, {
      useJwtTenant,
      devicesOnline,
      signal,
    }).catch(() => null),
  ]);
  return { slots, shellCtx };
}

/**
 * 静默并行：农场 KPI 补齐 + 设备/IoT。
 * 任意一项失败不影响另一项。
 */
export async function loadWelcomeSilentParallel(shellCtx, options = {}) {
  const [farmFull, devices] = await Promise.all([
    shellCtx
      ? enrichHomeFarmPreview(shellCtx, options).catch(() => shellCtx?.preview || null)
      : Promise.resolve(null),
    loadWelcomeDevices().catch(() => ({
      devices: [],
      iotDevices: [],
      devicesOnline: 0,
    })),
  ]);
  return { farmFull, devices };
}

export function loadWelcomeSilentDevices() {
  return loadWelcomeDevices();
}
